import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import { eventsAPI } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import { Plus, Edit, Trash2, Bold, Italic, List, Link as LinkIcon, Image as ImageIcon } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const AdminEvents = () => {
  const [events, setEvents] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    description: '',
    content: '',
    image: '',
    event_date: '',
    location: '',
    is_active: true,
  });
  const { toast } = useToast();

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      const response = await eventsAPI.getAll();
      setEvents(response.data);
    } catch (error) {
      toast({ title: 'Hata', description: 'Haberler yüklenemedi', variant: 'destructive' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEvent) {
        await eventsAPI.update(editingEvent._id, formData);
        toast({ title: 'Başarılı', description: 'Haber güncellendi' });
      } else {
        await eventsAPI.create(formData);
        toast({ title: 'Başarılı', description: 'Haber eklendi' });
      }
      setIsDialogOpen(false);
      resetForm();
      loadEvents();
    } catch (error) {
      console.error('Event update error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Haber kaydedilemedi';
      toast({ title: 'Hata', description: errorMsg, variant: 'destructive' });
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    
    // Convert event_date to YYYY-MM-DD format for date input
    let formattedDate = event.event_date || '';
    if (formattedDate) {
      try {
        const date = new Date(formattedDate);
        if (!isNaN(date.getTime())) {
          // Format to YYYY-MM-DD
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          formattedDate = `${year}-${month}-${day}`;
        }
      } catch (error) {
        console.error('Date formatting error:', error);
      }
    }
    
    const formattedData = {
      title: event.title || '',
      slug: event.slug || '',
      description: event.description || '',
      content: event.content || '',
      image: event.image || '',
      event_date: formattedDate,
      location: event.location || '',
      is_active: event.is_active !== undefined ? event.is_active : true,
    };
    
    setFormData(formattedData);
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Bu haberi silmek istediğinizden emin misiniz?')) {
      try {
        await eventsAPI.delete(id);
        toast({ title: 'Başarılı', description: 'Haber silindi' });
        loadEvents();
      } catch (error) {
        toast({ title: 'Hata', description: 'Haber silinemedi', variant: 'destructive' });
      }
    }
  };

  const resetForm = () => {
    setEditingEvent(null);
    setFormData({
      title: '',
      slug: '',
      description: '',
      content: '',
      image: '',
      event_date: '',
      location: '',
      is_active: true,
    });
  };

  const generateSlug = (title) => {
    const turkishMap = {
      'ç': 'c', 'ğ': 'g', 'ı': 'i', 'ö': 'o', 'ş': 's', 'ü': 'u',
      'Ç': 'c', 'Ğ': 'g', 'İ': 'i', 'Ö': 'o', 'Ş': 's', 'Ü': 'u'
    };
    return title
      .split('')
      .map(char => turkishMap[char] || char)
      .join('')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const insertHtmlTag = (tag) => {
    const textarea = document.getElementById('content-editor');
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = formData.content.substring(start, end);
    
    let insertText = '';
    switch(tag) {
      case 'h2':
        insertText = `<h2>${selectedText || 'Başlık'}</h2>`;
        break;
      case 'h3':
        insertText = `<h3>${selectedText || 'Alt Başlık'}</h3>`;
        break;
      case 'bold':
        insertText = `<strong>${selectedText || 'Kalın metin'}</strong>`;
        break;
      case 'italic':
        insertText = `<em>${selectedText || 'İtalik metin'}</em>`;
        break;
      case 'ul':
        insertText = `<ul>\n  <li>${selectedText || 'Liste öğesi'}</li>\n  <li>Liste öğesi 2</li>\n</ul>`;
        break;
      case 'link':
        insertText = `<a href="URL">${selectedText || 'Link metni'}</a>`;
        break;
      case 'image':
        insertText = `<img src="GORSEL_URL" alt="${selectedText || 'Resim açıklaması'}" />`;
        break;
      case 'p':
        insertText = `<p>${selectedText || 'Paragraf metni'}</p>`;
        break;
    }
    
    const newContent = formData.content.substring(0, start) + insertText + formData.content.substring(end);
    setFormData({ ...formData, content: newContent });
  };


  const handleTitleChange = (e) => {
    const title = e.target.value;
    setFormData({
      ...formData,
      title,
      slug: generateSlug(title)
    });
  };

  return (
    <AdminLayout>
      <div>
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-white">Haber Yönetimi</h1>
          <Button
            onClick={() => {
              resetForm();
              setIsDialogOpen(true);
            }}
            className="bg-[#FDB714] hover:bg-[#e5a510] text-black font-semibold"
          >
            <Plus size={20} className="mr-2" />
            Yeni Haber
          </Button>
        </div>

        <div className="space-y-4">
          {events.map((event) => (
            <Card key={event._id} className="bg-gray-900 border-gray-800 p-6">
              <div className="flex gap-6">
                <img src={event.image} alt={event.title} className="w-32 h-32 object-cover rounded-lg" />
                <div className="flex-1">
                  <h3 className="text-white font-bold text-xl mb-2">{event.title}</h3>
                  <p className="text-gray-400 mb-3">{event.description}</p>
                  <div className="flex gap-4 text-sm text-gray-500">
                    <span>Tarih: {event.event_date}</span>
                    <span>Lokasyon: {event.location}</span>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEdit(event)}
                    className="border-gray-700 text-gray-300 hover:bg-gray-800"
                  >
                    <Edit size={16} />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(event._id)}
                    className="border-red-700 text-red-500 hover:bg-red-900/20"
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingEvent ? 'Haber Düzenle' : 'Yeni Haber Ekle'}</DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto">
              <div>
                <Label>Başlık</Label>
                <Input
                  value={formData.title}
                  onChange={handleTitleChange}
                  className="bg-gray-800 border-gray-700"
                  required
                />
              </div>

              <div>
                <Label>Slug (URL)</Label>
                <Input
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  className="bg-gray-800 border-gray-700"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">URL: /haberler/{formData.slug}</p>
              </div>

              <div>
                <Label>Kısa Açıklama (Liste görünümü için)</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="bg-gray-800 border-gray-700"
                  rows={3}
                  required
                />
              </div>

              <div>
                <Label>Detaylı İçerik (HTML ile yazabilirsiniz)</Label>
                <div className="mb-2 flex flex-wrap gap-2">
                  <Button type="button" size="sm" variant="outline" onClick={() => insertHtmlTag('h2')} className="text-xs">
                    H2 Başlık
                  </Button>
                  <Button type="button" size="sm" variant="outline" onClick={() => insertHtmlTag('h3')} className="text-xs">
                    H3 Başlık
                  </Button>
                  <Button type="button" size="sm" variant="outline" onClick={() => insertHtmlTag('p')} className="text-xs">
                    Paragraf
                  </Button>
                  <Button type="button" size="sm" variant="outline" onClick={() => insertHtmlTag('bold')} className="text-xs">
                    <Bold size={14} />
                  </Button>
                  <Button type="button" size="sm" variant="outline" onClick={() => insertHtmlTag('italic')} className="text-xs">
                    <Italic size={14} />
                  </Button>
                  <Button type="button" size="sm" variant="outline" onClick={() => insertHtmlTag('ul')} className="text-xs">
                    <List size={14} />
                  </Button>
                  <Button type="button" size="sm" variant="outline" onClick={() => insertHtmlTag('link')} className="text-xs">
                    <LinkIcon size={14} />
                  </Button>
                  <Button type="button" size="sm" variant="outline" onClick={() => insertHtmlTag('image')} className="text-xs">
                    <ImageIcon size={14} />
                  </Button>
                </div>
                <Textarea
                  id="content-editor"
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  className="bg-gray-800 border-gray-700 font-mono text-sm"
                  rows={12}
                  placeholder="HTML etiketleri ile içerik yazabilirsiniz: <h2>, <p>, <strong>, <ul>, <li>, <a>, <img> vb."
                />
                <p className="text-xs text-gray-500 mt-1">
                  Yukarıdaki butonlar ile HTML etiketleri ekleyebilir veya direkt HTML yazabilirsiniz.
                </p>
              </div>

              <div>
                <Label>Görsel URL</Label>
                <Input
                  value={formData.image}
                  onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                  className="bg-gray-800 border-gray-700"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Tarih</Label>
                  <Input
                    type="date"
                    value={formData.event_date}
                    onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
                    className="bg-gray-800 border-gray-700"
                    required
                  />
                </div>
                <div>
                  <Label>Lokasyon</Label>
                  <Input
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="bg-gray-800 border-gray-700"
                    required
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4"
                />
                <Label htmlFor="is_active">Haber Aktif</Label>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="submit" className="flex-1 bg-[#FDB714] hover:bg-[#e5a510] text-black font-semibold">
                  {editingEvent ? 'Güncelle' : 'Ekle'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                  className="border-gray-700 text-gray-300 hover:bg-gray-800"
                >
                  İptal
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminEvents;
